// T060: Faction Effect Mapper
// Assigns effects to cards based on faction characteristics

import type { Faction, CardRarity, EffectKeyword, CardDefinition } from '../types';
import { getLogger } from '../utils/logger';

const log = getLogger('FactionEffectMapper');

/**
 * Faction characteristic effects mapping.
 * Each faction has thematic effects that match their playstyle.
 */
const FACTION_EFFECTS: Record<Faction, { primary: EffectKeyword[]; secondary: EffectKeyword[] }> = {
  // Neutral categories
  neutral_spell: { primary: [], secondary: [] },
  neutral_unit: { primary: ['reward'], secondary: [] },

  // Special factions (00系列)
  power_beast: {
    primary: ['charge', 'overload'],
    secondary: ['electrify'],
  },
  carbon: {
    primary: ['transform', 'stackMerge'],
    secondary: ['charge'],
  },

  // Standard factions (01-16)
  fairy: {
    primary: ['stealth', 'ambush'],
    secondary: ['poison', 'haunt'],
  },
  artwork: {
    primary: ['aura', 'transform'],
    secondary: ['magicResist'],
  },
  traveler: {
    primary: ['slide', 'ambush'],
    secondary: ['reward'],
  },
  savage: {
    primary: ['devourMerge', 'pierce'],
    secondary: ['armorBreak', 'multiAttack'],
  },
  orc: {
    primary: ['taunt', 'armorBreak'],
    secondary: ['block', 'multiAttack'],
  },
  cat: {
    primary: ['ambush', 'stealth'],
    secondary: ['slide', 'reward'],
  },
  candy: {
    primary: ['reward', 'aura'],
    secondary: ['poison', 'charge'],
  },
  robot: {
    primary: ['mechaMerge', 'dismantle'],
    secondary: ['ranged', 'cover'],
  },
  skeleton: {
    primary: ['soul', 'revive'],
    secondary: ['haunt', 'decay'],
  },
  steam: {
    primary: ['board', 'transport'],
    secondary: ['ranged', 'cover'],
  },
  goat: {
    primary: ['charge', 'block'],
    secondary: ['taunt', 'cover'],
  },
  monster: {
    primary: ['parasiteMerge', 'poison'],
    secondary: ['freeze', 'stun'],
  },
  virtual: {
    primary: ['transform', 'summon'],
    secondary: ['stealth', 'target'],
  },
  hero: {
    primary: ['pierce', 'protect'],
    secondary: ['block', 'cover'],
  },
  island: {
    primary: ['board', 'embark'],
    secondary: ['slide', 'reward'],
  },
  circus: {
    primary: ['ambush', 'transform'],
    secondary: ['aura', 'slide'],
  },
};

/**
 * Effect distribution by rarity.
 * Higher rarity = more effects and access to secondary effects.
 */
const RARITY_EFFECT_COUNT: Record<CardRarity, { primary: number; secondary: number }> = {
  N: { primary: 0, secondary: 0 },    // No effects
  R: { primary: 1, secondary: 0 },    // 1 primary effect
  SR: { primary: 1, secondary: 1 },   // 1 primary + 1 secondary
  SSR: { primary: 2, secondary: 1 },  // 2 primary + 1 secondary
  SSSR: { primary: 2, secondary: 2 }, // 2 primary + 2 secondary
};

/**
 * Deterministically selects effects based on card ID.
 * Uses a simple hash to ensure consistent effect assignment.
 */
function hashCardId(cardId: string): number {
  let hash = 0;
  for (let i = 0; i < cardId.length; i++) {
    const char = cardId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

/**
 * Select N effects from an array based on a hash seed.
 */
function selectEffects(
  effects: EffectKeyword[],
  count: number,
  seed: number
): EffectKeyword[] {
  if (effects.length === 0 || count === 0) return [];

  const selected: EffectKeyword[] = [];
  const available = [...effects];

  for (let i = 0; i < count && available.length > 0; i++) {
    const index = (seed + i * 7) % available.length;
    selected.push(available[index]);
    available.splice(index, 1);
  }

  return selected;
}

/**
 * Normalize rarity values from raw data.
 * Handles '-', 'Promo,R', etc. by extracting valid rarity or defaulting to 'N'.
 */
function normalizeRarity(rawRarity: string): CardRarity {
  // Check for known rarities (exact match or contained in comma-separated values)
  const validRarities: CardRarity[] = ['SSSR', 'SSR', 'SR', 'R', 'N'];
  for (const rarity of validRarities) {
    if (rawRarity === rarity || rawRarity.includes(rarity)) {
      return rarity;
    }
  }
  // Default to 'N' (no effects) for unknown rarities like '-'
  return 'N';
}

export function getEffectsForCard(
  cardId: string,
  faction: string,
  rarity: string
): EffectKeyword[] {
  // Check if faction is a valid key in FACTION_EFFECTS
  if (!(faction in FACTION_EFFECTS)) {
    log.warn(`Unknown faction: ${faction}, cardId: ${cardId}`);
    return [];
  }

  const factionEffects = FACTION_EFFECTS[faction as Faction];
  const normalizedRarity = normalizeRarity(rarity);
  const distribution = RARITY_EFFECT_COUNT[normalizedRarity];

  const seed = hashCardId(cardId);

  const primaryEffects = selectEffects(
    factionEffects.primary,
    distribution.primary,
    seed
  );

  const secondaryEffects = selectEffects(
    factionEffects.secondary,
    distribution.secondary,
    seed + 1000
  );

  return [...primaryEffects, ...secondaryEffects];
}

/**
 * Apply effects to a card definition.
 */
export function applyEffectsToDefinition(definition: CardDefinition): CardDefinition {
  // Skip spell cards
  if (definition.type !== 'unit') return definition;

  // Skip if already has effects
  if (definition.effectIds && definition.effectIds.length > 0) {
    return definition;
  }

  const effects = getEffectsForCard(
    definition.id,
    definition.faction,
    definition.rarity ?? 'N'
  );

  if (effects.length === 0) return definition;

  return {
    ...definition,
    effectIds: effects,
  };
}

/**
 * Apply effects to all card definitions.
 */
export function applyEffectsToAllDefinitions(
  definitions: Map<string, CardDefinition>
): Map<string, CardDefinition> {
  const result = new Map<string, CardDefinition>();

  for (const [id, definition] of definitions) {
    result.set(id, applyEffectsToDefinition(definition));
  }

  return result;
}

/**
 * Get effect description in Chinese.
 */
export function getEffectDescriptionZh(effect: EffectKeyword): string {
  const descriptions: Record<EffectKeyword, string> = {
    pierce: '貫穿：攻擊無法被阻擋',
    ranged: '遠程：可攻擊後排',
    armorBreak: '破甲：忽視防禦效果',
    slide: '滑壘：戰鬥後移動',
    ambush: '伏擊：召喚後立即攻擊',
    multiAttack: '多重攻擊：攻擊多個目標',
    block: '格擋：阻擋鄰近攻擊',
    cover: '掩護：保護後排單位',
    electrify: '通電：反擊傷害',
    taunt: '嘲諷：必須先攻擊',
    stealth: '隱匿：無法被指定',
    protect: '保護：免疫破壞',
    poison: '毒：目標送入森林',
    dismantle: '分解：機械送入森林',
    freeze: '冰凍：無法行動',
    burn: '燃燒：持續傷害',
    stun: '暈眩：跳過行動',
    mechaMerge: '機械合體：與機械合體',
    devourMerge: '吞噬合體：吸收ATK',
    parasiteMerge: '寄生合體：附著敵方',
    stackMerge: '堆疊合體：堆疊卡片',
    reward: '賞金：被破壞時抽牌',
    charge: '充能：獲得ATK加成',
    drain: '吸取：吸收敵方ATK',
    overload: '填充：可放入更多同名卡',
    magicResist: '魔法抗性：免疫法術',
    destroyResist: '破壞抗性：免疫效果破壞',
    effectResist: '效果抗性：免疫負面效果',
    bind: '束縛：目標無法移動',
    tap: '橫置：使單位橫置',
    decay: '衰敗：降低ATK',
    silence: '沉默：效果失效',
    board: '搭乘：可搭乘載具',
    disembark: '下車：離開載具',
    embark: '上車：進入載具',
    transport: '運輸：可運載單位',
    soul: '靈魂：可復活',
    revive: '復活：從墓地返回',
    haunt: '纏繞：持續影響敵方',
    transform: '變形：改變形態',
    target: '指定：選擇目標',
    aura: '光環：影響周圍單位',
    summon: '召喚：生成新單位',
    flying: '飛行：可放置任意位置',
    vanish: '消逝：使用後移除遊戲',
  };

  return descriptions[effect];
}

/**
 * Get faction theme description in Chinese.
 */
export function getFactionThemeZh(faction: Faction): string {
  const themes: Record<Faction, string> = {
    neutral_spell: '中立魔法',
    neutral_unit: '中立單位',
    power_beast: '發電獸 - 充能與電擊',
    carbon: '碳碳族 - 變形與堆疊',
    fairy: '妖精 - 隱匿與伏擊',
    artwork: '藝術品 - 光環與變形',
    traveler: '特殊旅人 - 移動與賞金',
    savage: '進化野人 - 吞噬與貫穿',
    orc: '獸人 - 嘲諷與破甲',
    cat: '喵喵賊 - 伏擊與偷竊',
    candy: '甜點王國 - 賞金與光環',
    robot: '機械軍團 - 合體與分解',
    skeleton: '骷髏人 - 靈魂與復活',
    steam: '蒸汽世界 - 搭乘與運輸',
    goat: '山羊族 - 充能與防禦',
    monster: '妖怪村 - 寄生與控制',
    virtual: '虛擬 - 變形與召喚',
    hero: '魔王討伐隊 - 貫穿與保護',
    island: '歡樂島 - 搭乘與移動',
    circus: '馬戲團 - 伏擊與變形',
  };

  return themes[faction];
}

// Export singleton functions for convenience
export const FactionEffectMapper = {
  getEffectsForCard,
  applyEffectsToDefinition,
  applyEffectsToAllDefinitions,
  getEffectDescriptionZh,
  getFactionThemeZh,
};
